import React from 'react';
import { Link } from 'react-router-dom';
import {
  homeKeywords,
  nice06Keywords,
  varToulonKeywords,
} from '../topKeywords';
import { blogPosts, type BlogPost } from '../blogPosts';

const heroBullets = [
  'Débarras, tri, désinfection certifiée',
  'Intervention discrète 7j/7 en urgence',
  'Traitement odeurs, nuisibles et déchets',
];

const services = [
  {
    title: 'Nettoyage logement insalubre',
    description:
      'Remise en état complète d’appartements et maisons insalubres après accumulation de déchets, sinistre ou abandon.',
  },
  {
    title: 'Nettoyage syndrome de Diogène',
    description:
      'Accompagnement discret des familles et syndics pour le nettoyage extrême des logements touchés par le syndrome de Diogène.',
  },
  {
    title: 'Nettoyage extrême',
    description:
      'Interventions lourdes avec évacuation massive de déchets, décontamination, désinfection et désodorisation profonde.',
  },
  {
    title: 'Gestion des déchets',
    description:
      'Tri, évacuation vers déchetterie agréée et traçabilité complète des déchets et encombrants retirés du logement.',
  },
];

const interventionCities = [
  'Nice',
  'Cannes',
  'Antibes',
  'Grasse',
  'Menton',
  'Toulon',
  'Fréjus',
  'Draguignan',
  'La Seyne-sur-Mer',
  'Hyères',
];

export const Home: React.FC = () => {
  return (
    <div className="bg-slate-50">
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12 lg:py-20 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-mint mb-3">
              Intervention urgente 24h – Alpes Maritimes 06 & Var 83
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4">
              Nettoyage logement insalubre Alpes Maritimes Var Nice Toulon 24h
            </h1>
            <p className="text-sm sm:text-base text-slate-200 mb-5 max-w-xl">
              Entreprise spécialisée en{' '}
              <strong>
                nettoyage de logements insalubres, syndrome de Diogène et
                nettoyage extrême
              </strong>{' '}
              dans les Alpes Maritimes (06) et le Var (83). Intervention rapide,
              discrète et clé en main.
            </p>
            <ul className="space-y-2 mb-6 text-sm">
              {heroBullets.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 h-4 w-4 rounded-full bg-mint flex items-center justify-center text-[10px] text-slate-950">
                    ✓
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://wa.me/33745171331?text=Bonjour%2C+je+souhaite+un+devis+pour+un+nettoyage+logement+insalubre+dans+le+06+ou+83."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-mint px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 transition-colors"
              >
                Devis WhatsApp sous 2h
              </a>
              <Link
                to="/tarifs"
                className="inline-flex items-center justify-center rounded-md border border-slate-600 px-4 py-2 text-xs sm:text-sm font-medium text-slate-100 hover:bg-slate-800 transition-colors"
              >
                Voir les tarifs moyens
              </Link>
              <p className="w-full text-xs text-slate-400">
                Basé sur les requêtes :{' '}
                <em>
                  nettoyage logement insalubre, nettoyage extrême, nettoyage
                  syndrome de Diogène
                </em>
                .
              </p>
            </div>
          </div>

          <div className="relative">
            <img
              src="/images/hero-nettoyage-logement-insalubre-06-var-avant-apres.png"
              alt="Avant après nettoyage logement insalubre Nice Toulon Alpes Maritimes Var"
              className="aspect-video w-full rounded-2xl object-cover border border-slate-700"
              loading="lazy"
            />
            <div className="absolute -bottom-5 -right-4 bg-white/95 border border-slate-200 rounded-xl px-4 py-3 shadow-lg max-w-xs">
              <p className="text-xs font-semibold text-slate-800 mb-1">
                +120 logements insalubres remis en état
              </p>
              <p className="text-[11px] text-slate-500">
                Dossiers gérés pour particuliers, notaires, agences et syndics
                sur le 06 et le 83.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12 lg:py-16">
        <div className="grid gap-8 lg:grid-cols-[2fr,1.3fr] items-start">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4">
              Services de nettoyage logement insalubre, Diogène et nettoyage
              extrême
            </h2>
            <p className="text-sm text-slate-600 mb-5">
              Nous intervenons sur tous types de situations : accumulation
              extrême de déchets, insalubrité, odeurs tenaces, présence de
              nuisibles, après décès ou abandon de logement. Chaque intervention
              est préparée avec un devis détaillé et un plan d’action adapté.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-xs text-slate-600">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">
              Zones d’intervention Alpes Maritimes 06 & Var 83
            </h3>
            <p className="text-xs text-slate-600 mb-3">
              Nous couvrons l’ensemble de la Côte d’Azur, de{' '}
              <strong>Nice</strong> à <strong>Toulon</strong>, pour le{' '}
              <strong>nettoyage de logements insalubres</strong> et le{' '}
              <strong>nettoyage syndrome de Diogène</strong>.
            </p>
            <ul className="flex flex-wrap gap-2 mb-4">
              {interventionCities.map((city) => (
                <li
                  key={city}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700"
                >
                  {city}
                </li>
              ))}
            </ul>
            <div className="rounded-lg bg-slate-900 text-slate-100 p-3 text-[11px]">
              <p className="font-semibold mb-1">
                Besoin d’un nettoyage logement insalubre à Nice ou Toulon ?
              </p>
              <p className="mb-2">
                Mentionnez la ville et la surface du logement dans votre
                demande de devis pour une estimation plus rapide.
              </p>
              <Link
                to="/devis"
                className="inline-flex items-center justify-center rounded-md bg-mint px-3 py-1.5 text-[11px] font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
              >
                Demander un devis détaillé
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-12 lg:pb-16">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3">
              Pourquoi optimiser votre recherche « nettoyage logement insalubre
              » sur le 06 et le 83 ?
            </h2>
            <p className="text-sm text-slate-600 mb-3">
              Les recherches comme{' '}
              <strong>« nettoyage logement insalubre »</strong>,{' '}
              <strong>« nettoyage logement insalubre Nice »</strong> ou{' '}
              <strong>« nettoyage maison insalubre Var »</strong> traduisent une
              urgence réelle : logement impossible à habiter, menace
              d’expulsion, conflit avec le voisinage ou besoin d’un état des
              lieux rapide pour une vente.
            </p>
            <p className="text-sm text-slate-600 mb-3">
              Nous avons structuré ce site autour de ces requêtes, en utilisant
              les principaux mots-clés issus de l’étude Semrush pour vous
              proposer un contenu clair, localisé et orienté résultats.
            </p>
            <p className="text-sm text-slate-600">
              Que vous soyez <strong>propriétaire, locataire, notaire, agence
              immobilière ou syndic</strong>, nous proposons un accompagnement
              complet jusqu’à la remise en état du bien et la fourniture de
              photos avant/après.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Principaux mots-clés ciblés
            </h3>
            <p className="mb-1 font-medium text-slate-800">Accueil / global :</p>
            <ul className="list-disc list-inside mb-2">
              {homeKeywords.slice(0, 4).map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
            <p className="mb-1 font-medium text-slate-800">Nice 06 :</p>
            <ul className="list-disc list-inside mb-2">
              {nice06Keywords.slice(0, 3).map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
            <p className="mb-1 font-medium text-slate-800">Var / Toulon :</p>
            <ul className="list-disc list-inside">
              {varToulonKeywords.slice(0, 3).map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-12 lg:pb-16">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">
          Questions fréquentes sur le nettoyage logement insalubre
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Quel est le prix d’un nettoyage logement insalubre ?
            </h3>
            <p>
              Le tarif dépend de la surface, du volume de déchets, du niveau
              d’insalubrité et de l’accessibilité du logement. À titre
              indicatif, un studio très sale démarre vers 800&nbsp;€, un
              appartement T2/T3 insalubre entre 1&nbsp;500&nbsp;€ et
              3&nbsp;000&nbsp;€, et une grande maison très dégradée entre
              3&nbsp;000&nbsp;€ et 6&nbsp;000&nbsp;€ et plus.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Intervenez-vous en urgence dans le 06 et le 83 ?
            </h3>
            <p>
              Oui, nous intervenons en urgence 7j/7 sur les Alpes Maritimes
              (06) et le Var (83) pour les logements insalubres, les cas de
              syndrome de Diogène et les nettoyages extrêmes. Contact direct
              par WhatsApp au{' '}
              <a
                href="https://wa.me/33745171331"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-semibold"
              >
                +33&nbsp;7&nbsp;45&nbsp;17&nbsp;13&nbsp;31
              </a>
              .
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Comment obtenir un devis nettoyage logement insalubre ?
            </h3>
            <p>
              Envoyez des photos des pièces principales (cuisine, salle de
              bain, pièces de vie, couloirs, cave/garage) avec la surface et la
              ville (Nice, Cannes, Antibes, Toulon, Fréjus, etc.). Vous pouvez
              transmettre ces éléments directement par WhatsApp pour un retour
              rapide.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Travaillez-vous avec les notaires, agences et syndics ?
            </h3>
            <p>
              Oui, nous accompagnons régulièrement notaires, agences
              immobilières et syndics pour la remise en état de logements
              insalubres avant vente, succession, état des lieux ou relocation,
              avec photos avant/après et factures détaillées.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
            Derniers articles sur le nettoyage logement insalubre
          </h2>
          <Link
            to="/blog"
            className="text-xs font-medium text-primary hover:text-blue-800 transition-colors"
          >
            Voir tous les articles →
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {blogPosts.slice(0, 3).map((post: BlogPost) => (
            <article
              key={post.slug}
              className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700"
            >
              <p className="text-[10px] uppercase tracking-wide text-primary font-semibold mb-1">
                {post.category}
              </p>
              <Link
                to={`/blog/${post.slug}`}
                className="block text-[13px] font-semibold text-slate-900 mb-1 hover:text-primary transition-colors"
              >
                {post.title}
              </Link>
              <p className="line-clamp-3">{post.description}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};
